import React, { useMemo, useState, useEffect } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import ViewGroups from "./Groups/ViewGroups";
import Matches from "./Matchs/Matches";
import ViewBracket from "./Bracket/ViewBracket";
import { Tabs, LoadingState, Toast, Button } from "./ui";
import { appConstants } from "./_constants/app.constants";
import { useAuth } from "./auth/AuthContext";
import "./viewer.css";

const DISPLAY_TIME_MS = 30000;
const TRANSITION_TIME_MS = 650;

export default function Viewer() {
  const [currentComponent, setCurrentComponent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [paused, setPaused] = useState(false);
  const { tournamentId } = useParams();
  const { ready } = useAuth();
  const [searchParams] = useSearchParams();
  const showOperatorControls = searchParams.get("controls") === "1";
  const tabsIdPrefix = "viewer-tabs";

  const [viewerGate, setViewerGate] = useState("loading");

  useEffect(() => {
    if (!ready) return undefined;
    if (!tournamentId) {
      setViewerGate("missing");
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`${appConstants.API_URL}/api/tournaments/${tournamentId}/access`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.canAccessViewer) setViewerGate("ok");
        else setViewerGate("denied");
      })
      .catch(() => {
        if (!cancelled) setViewerGate("denied");
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tournamentId]);

  const tabs = useMemo(
    () => [
      { key: "groups", label: "Grupos" },
      { key: "matches", label: "Partidos" },
      { key: "bracket", label: "Fase final" },
    ],
    []
  );

  useEffect(() => {
    if (paused || !showOperatorControls) return undefined;

    const startTransition = () => {
      setTransitioning(true);
      window.setTimeout(() => {
        setCurrentComponent((prev) => (prev + 1) % 3);
        setTransitioning(false);
      }, TRANSITION_TIME_MS);
    };

    const timer = window.setInterval(startTransition, DISPLAY_TIME_MS + TRANSITION_TIME_MS);
    return () => window.clearInterval(timer);
  }, [paused, showOperatorControls]);

  const handleNavigation = (index) => {
    if (index === currentComponent || transitioning) return;
    setTransitioning(true);
    window.setTimeout(() => {
      setCurrentComponent(index);
      setTransitioning(false);
    }, 250);
  };

  const renderComponent = () => {
    if (currentComponent === 0) return <ViewGroups />;
    if (currentComponent === 1) return <Matches />;
    return <ViewBracket />;
  };

  if (!ready || viewerGate === "loading") {
    return <LoadingState label="Comprobando acceso al visor…" className="min-h-screen" />;
  }

  if (viewerGate === "missing" || !tournamentId) {
    return (
      <div className="viewer-container flex min-h-screen flex-col items-center justify-center gap-6 bg-[#f3efe4] px-4 py-12">
        <Toast kind="warning" className="max-w-md text-center">
          Falta el identificador del torneo en la URL. Abre el visor desde el menú del torneo (enlace que incluye el id) o pide al organizador el enlace completo; no se elige un torneo automáticamente.
        </Toast>
        <div className="flex flex-wrap justify-center gap-3">
          <Button as={RouterLink} to="/" variant="primary">
            Volver al inicio
          </Button>
          <Button as={RouterLink} to="/login" variant="outline" state={{ from: { pathname: "/" } }}>
            Iniciar sesión
          </Button>
        </div>
      </div>
    );
  }

  if (viewerGate === "denied" && tournamentId) {
    return (
      <div className="viewer-container flex min-h-screen flex-col items-center justify-center gap-6 bg-[#f3efe4] px-4 py-12">
        <Toast kind="warning" className="max-w-md text-center">
          El modo pantalla completa solo está disponible para cuentas invitadas con rol <strong>Viewer</strong> o para el
          staff del torneo. Consulta al organizador o abre grupos y partidos desde el menú público.
        </Toast>
        <div className="flex flex-wrap justify-center gap-3">
          <Button as={RouterLink} to="/login" variant="primary" state={{ from: { pathname: `/viewer/${tournamentId}` } }}>
            Iniciar sesión
          </Button>
          <Button as={RouterLink} to={`/tournamentactions/${tournamentId}`} variant="outline">
            Menú del torneo
          </Button>
          <Button as={RouterLink} to="/" variant="ghost">
            Inicio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer-container relative min-h-screen bg-[#f3efe4]">
      <div className="sticky top-0 z-20 border-b border-arena/15 bg-white/90 px-3 py-2 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Tabs
            items={tabs}
            active={currentComponent}
            onChange={handleNavigation}
            idPrefix={tabsIdPrefix}
            ariaLabel="Secciones del visor"
          />
          {showOperatorControls ? (
            <button
              type="button"
              onClick={() => setPaused((v) => !v)}
              className="focus-ring self-end rounded-xl border border-arena/20 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-ink-soft hover:bg-arena/5"
            >
              {paused ? "Reanudar" : "Pausar ciclo"}
            </button>
          ) : null}
        </div>
      </div>

      <div className="min-h-[calc(100vh-68px)]">
        {transitioning ? (
          <LoadingState label="Cambiando panel..." className="min-h-[calc(100vh-68px)]" />
        ) : (
          <div
            id={`${tabsIdPrefix}-panel-${currentComponent}`}
            role="tabpanel"
            aria-labelledby={`${tabsIdPrefix}-tab-${currentComponent}`}
            className="h-full w-full overflow-auto"
          >
            {renderComponent()}
          </div>
        )}
      </div>

      {showOperatorControls && tournamentId ? (
        <p className="pointer-events-none fixed bottom-3 right-3 z-10 hidden rounded-lg bg-black/45 px-2 py-1 text-xs font-semibold text-white/90 md:block">
          Torneo: {tournamentId.slice(0, 8)}...
        </p>
      ) : null}
    </div>
  );
}
